import { memo, useMemo } from 'react';
import type { HeatmapGrid } from '@4cm/core/tactics/overlays';

/**
 * §17 — Heatmap (player density / territory) overlay for the 2D
 * tactics board.
 *
 * The grid arrives from `computeHeatmap()` as a row-major Float32Array
 * of normalised intensities in [0, 1] over a 2 m pitch tessellation
 * (centred on origin). Each cell is rendered as one SVG `<rect>` in
 * the canonical landscape mapping the rest of the 2D board uses:
 *   SVG-x = -world.z
 *   SVG-y =  world.x
 * which means a length-axis grid cell becomes a width-axis SVG rect
 * (and vice versa). Width / height of each rect is the world `step`
 * (the SVG and world axes share a unit-metres convention here, so we
 * don't need a separate scale factor).
 *
 * Colour ramp — cool→hot, 4 stops, simple linear interpolation:
 *   0.00 → cool blue   (low density)
 *   0.40 → green       (mid)
 *   0.70 → yellow      (warm)
 *   1.00 → red         (peak)
 * The ramp mirrors broadcast convention (Sky / Hawk-Eye / Next Gen
 * Stats) where peak zones glow red and the rest fades through warm
 * tones into a cool ambient.
 *
 * Cell alpha is `intensity * opacity` so cool zones fade out
 * completely — the heatmap reads as warm hot-spots over the pitch
 * rather than a uniform tinted sheet. We cull cells where
 * `intensity < INTENSITY_CULL_THRESHOLD` to keep the SVG element
 * count down on 105×68 m pitches with a 2 m grid (~1.8k cells max
 * unculled; typical 22-token board renders ~30–60% of those).
 */

/** Below this normalised intensity, the cell is skipped entirely
 *  rather than rendered with near-zero alpha. Tuned for a balance
 *  between visual fidelity (you can still see the cool-blue tail) and
 *  SVG node count. Exported for the test file. */
export const INTENSITY_CULL_THRESHOLD = 0.05;

interface RampStop {
  /** Position along the ramp in [0, 1]. */
  t: number;
  /** RGB triple [0, 255]. */
  rgb: [number, number, number];
}

/** 4-stop cool→hot ramp. Order matters — must be ascending in `t`. */
const RAMP: RampStop[] = [
  { t: 0.0, rgb: [56, 130, 232] },   // cool blue
  { t: 0.4, rgb: [74, 222, 128] },   // green (matches Voronoi home)
  { t: 0.7, rgb: [251, 191, 36] },   // yellow / amber (matches Voronoi away)
  { t: 1.0, rgb: [239, 68, 68] },    // red (peak density)
];

/**
 * Linearly interpolate the colour ramp at intensity `t ∈ [0, 1]`.
 * Returns an integer RGB triple suitable for `rgba(...)` output.
 */
function rampColor(t: number): [number, number, number] {
  // Clamp first — guards against NaN / out-of-range from upstream
  // (computeHeatmap normalises, but render-side safety is cheap).
  if (!(t > 0)) return RAMP[0]!.rgb;
  if (t >= 1) return RAMP[RAMP.length - 1]!.rgb;
  // Find the two stops that bracket t. Linear scan — RAMP has 4
  // entries, branch prediction makes this trivially fast.
  for (let i = 1; i < RAMP.length; i++) {
    const upper = RAMP[i]!;
    if (t <= upper.t) {
      const lower = RAMP[i - 1]!;
      const span = upper.t - lower.t;
      const f = span > 0 ? (t - lower.t) / span : 0;
      return [
        Math.round(lower.rgb[0] + (upper.rgb[0] - lower.rgb[0]) * f),
        Math.round(lower.rgb[1] + (upper.rgb[1] - lower.rgb[1]) * f),
        Math.round(lower.rgb[2] + (upper.rgb[2] - lower.rgb[2]) * f),
      ];
    }
  }
  // Unreachable — `t < 1` is handled by the loop above. Belt-and-
  // braces fallthrough so TS doesn't complain about the implicit
  // undefined return.
  return RAMP[RAMP.length - 1]!.rgb;
}

export interface HeatmapLayer2DProps {
  /** Grid produced by `computeHeatmap()`. May be `null` to mount the
   *  layer skeleton without rendering anything. */
  grid: HeatmapGrid | null;
  /** Caller-controlled opacity in [0, 1]. Multiplied into each cell's
   *  intensity-derived alpha. */
  opacity: number;
}

/** One renderable cell — pre-computed in a useMemo so React doesn't
 *  re-derive on every parent render. */
interface RenderCell {
  key: string;
  /** SVG x coordinate (top-left of the rect). */
  svgX: number;
  /** SVG y coordinate (top-left of the rect). */
  svgY: number;
  /** Fill in `rgba(r, g, b, a)` form — alpha already includes opacity. */
  fill: string;
  /** Intensity, kept on the DOM as `data-intensity` for test inspection. */
  intensity: number;
}

function HeatmapLayer2DInner({ grid, opacity }: HeatmapLayer2DProps) {
  // Clamp opacity once outside the loop.
  const op = opacity < 0 ? 0 : opacity > 1 ? 1 : opacity;

  const cells = useMemo<RenderCell[]>(() => {
    if (!grid) return [];
    const out: RenderCell[] = [];
    const { nRows, nCols, step, halfL, halfW, intensity } = grid;
    for (let r = 0; r < nRows; r++) {
      // World x-range for this row.
      const worldXMin = -halfW + r * step;
      // SVG-y mirrors world.x in the landscape mapping — same value.
      const svgY = worldXMin;
      for (let c = 0; c < nCols; c++) {
        const v = intensity[r * nCols + c]!;
        if (v < INTENSITY_CULL_THRESHOLD) continue;
        // World z-range for this column.
        const worldZMin = -halfL + c * step;
        // SVG-x = -world.z. Use the cell's max-z corner so the rect
        // grows from svgX → svgX + step in SVG space, matching the
        // -world.z mapping.
        const svgX = -(worldZMin + step);
        const [rr, gg, bb] = rampColor(v);
        const alpha = v * op;
        out.push({
          key: `${r}-${c}`,
          svgX,
          svgY,
          fill: `rgba(${rr}, ${gg}, ${bb}, ${alpha})`,
          intensity: v,
        });
      }
    }
    return out;
  }, [grid, op]);

  // Mount nothing if the grid is absent or every cell was culled.
  if (!grid || cells.length === 0) return null;

  return (
    <g
      className="t2d-heatmap-layer"
      data-testid="t2d-heatmap-layer"
      data-team={grid.team}
      pointerEvents="none"
      // Clipped to the pitch rectangle — see VoronoiLayer2D + PitchSvg.
      // Edge cells in the heatmap grid can extend slightly past the
      // touchline since the grid is sampled on whole-metre steps;
      // clipping keeps the density field tidy at the boundary.
      clipPath="url(#t2d-pitch-clip)"
    >
      {cells.map((cell) => (
        <rect
          key={cell.key}
          x={cell.svgX}
          y={cell.svgY}
          width={grid.step}
          height={grid.step}
          fill={cell.fill}
          data-intensity={cell.intensity.toFixed(3)}
          data-testid="t2d-heatmap-cell"
        />
      ))}
    </g>
  );
}

/** Memoised — the parent typically holds the grid reference stable
 *  across unrelated state changes (e.g. HUD toggles, scrubber moves
 *  that don't reshuffle positions). */
export const HeatmapLayer2D = memo(HeatmapLayer2DInner);
